import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ActivityIcon from '@mui/icons-material/LocalActivity';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import WalletIcon from '@mui/icons-material/Wallet';

import Account from '../hsbc_img/img/Account';
import AddNewCompany from '../hsbc_img/img/AddNewCompany';
import ProductsAndServices from '../hsbc_img/img/ProductsAndServices';
import TransactionsHistory from '../hsbc_img/img/TransactionsHistory';
import ViewGrid from '../hsbc_img/img/ViewGrid';
import type { NonEmptyArray } from '../utils/typescriptUtils';

import {
	companyHeaderTabsStrings,
	companySettingsCodingStrings,
	sideMenuStrings,
	teamHeaderTabsStrings,
} from './strings';

export const httpConstants = {
	timeout: 60_000,
};

export const supportAdmin = 'tradeshift_support_admin';
export const companyAdmin = 'company_admin';
export const teamAdmin = 'team_admin';
export const manager = 'manager';
export const teamMember = 'team_member';

export const defaultAllowedRoles = [supportAdmin, companyAdmin];

type SideMenuOption = {
	isCompanySettingsVisible: boolean;
	teamId: string;
};

const isAmexCardProvider = globalThis.cardProvider?.IS_AMEX_PROVIDER_SETUP;

export const sideMenuConstants = {
	sideMenuId: 'main-sideMenu',
	requestSidebarId: 'requestSidebar',
	teamSwitcherAsideId: 'teamSwitcherAside',
	sideMenuOptions: ({ isCompanySettingsVisible = false, teamId = '' }: SideMenuOption) => [
		{
			id: 'dashboard',
			name: sideMenuStrings.SIDE_MENU_DASHBOARD_OPTION,
			route: '/dashboard',
			icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? ViewGrid : DashboardIcon,
			roles: [...defaultAllowedRoles, teamAdmin, manager],
		},
		{
			id: 'wallet',
			name: sideMenuStrings.SIDE_MENU_WALLET_OPTION,
			route: '/wallet',
			icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? Account : WalletIcon,
			roles: [...defaultAllowedRoles, teamAdmin, manager, teamMember],
		},
		{
			id: 'activity',
			name: sideMenuStrings.SIDE_MENU_APPROVALS_OPTION,
			route: '/activity',
			icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? TransactionsHistory : ActivityIcon,
			roles: [...defaultAllowedRoles, teamAdmin, manager],
		},
		{
			id: 'requests',
			name: sideMenuStrings.SIDE_MENU_MY_REQUESTS_OPTION,
			route: '',
			icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? TransactionsHistory : RequestPageIcon,
			roles: [teamAdmin, manager, teamMember],
			hasAside: true,
		},
		...(isCompanySettingsVisible
			? [
					{
						id: 'manage-settlements',
						name: isAmexCardProvider
							? sideMenuStrings.SIDE_MENU_MANAGE_SETTLEMENTS_OPTION
							: sideMenuStrings.SIDE_MENU_MANAGE_TRANSACTIONS_OPTION,
						route: '/manage-settlements',
						icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? ProductsAndServices : ReceiptLongIcon,
						roles: [...defaultAllowedRoles, teamAdmin, manager],
					},
				]
			: [
					{
						id: 'team-manage-settlements',
						name: isAmexCardProvider
							? sideMenuStrings.SIDE_MENU_MANAGE_SETTLEMENTS_OPTION
							: sideMenuStrings.SIDE_MENU_MANAGE_TRANSACTIONS_OPTION,
						route: `/teams/${teamId}/manage-settlements`,
						icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? ProductsAndServices : ReceiptLongIcon,
						roles: [...defaultAllowedRoles, teamAdmin, manager],
					},
				]),
		{
			id: 'team-overview',
			name: sideMenuStrings.SIDE_MENU_TEAM_SETTINGS_OPTION,
			route: '/team/overview',
			icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? AddNewCompany : ManageAccountsIcon,
			roles: [teamAdmin],
		},
		{
			id: 'settings-overview',
			name: sideMenuStrings.SIDE_MENU_COMPANY_SETTINGS_OPTION,
			route: '/settings/overview',
			icon: globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP ? AddNewCompany : ApartmentIcon,
			roles: defaultAllowedRoles,
			nestedRoute: '/team',
		},
		{
			id: 'manage-settings-overview',
			name: sideMenuStrings.SIDE_MENU_SUPPORT_SETTINGS_OPTION,
			route: '/manage/settings',
			icon: AdminPanelSettingsIcon,
			roles: [supportAdmin],
			nestedRoute: '/team',
		},
	],
};

const pageRoutes = {
	companySettingsOverviewRoute: '/settings/overview',
	companySettingsTeamsRoute: '/settings/teams',
	companySettingsSourceCardsRoute: '/settings/cards',
	companySettingsCodingListsRoute: '/settings/coding',
	teamSettingsOverviewRoute: '/team/overview',
	teamSettingsSourceCardsRoute: '/team/cards',
	teamSettingsPeopleRoute: '/team/people',
	teamSettingsCodingListsRoute: '/team/coding',
	supportRoute: '/support',
	walletRoutes: '/wallet',
};

export const pageHeaderTabsConstants = (isAmexCardProvider: boolean) => {
	const companySettingsTabs = [
		{
			label: companyHeaderTabsStrings.COMPANY_SETTINGS_OVERVIEW_HEADER_TAB,
			link: pageRoutes.companySettingsOverviewRoute,
			tabIndex: 0,
			tabPath: pageRoutes.companySettingsOverviewRoute,
		},
		{
			label: companyHeaderTabsStrings.COMPANY_SETTINGS_TEAMS_HEADER_TAB,
			link: pageRoutes.companySettingsTeamsRoute,
			tabIndex: 1,
			tabPath: pageRoutes.companySettingsTeamsRoute,
		},
		{
			label: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
				? companyHeaderTabsStrings.COMPANY_SETTINGS_PHYSICAL_CARDS_HEADER_TAB
				: companyHeaderTabsStrings.COMPANY_SETTINGS_SOURCE_CARDS_HEADER_TAB,
			link: pageRoutes.companySettingsSourceCardsRoute,
			tabIndex: 2,
			tabPath: pageRoutes.companySettingsSourceCardsRoute,
		},
	];

	if (isAmexCardProvider) {
		companySettingsTabs.push({
			label: companyHeaderTabsStrings.COMPANY_SETTINGS_CODING_LISTS_HEADER_TAB,
			link: pageRoutes.companySettingsCodingListsRoute,
			tabIndex: 3,
			tabPath: pageRoutes.companySettingsCodingListsRoute,
		});
	}

	const teamSettingsTabs = [
		{
			label: teamHeaderTabsStrings.TEAM_SETTINGS_OVERVIEW_HEADER_TAB,
			link: pageRoutes.teamSettingsOverviewRoute,
			tabIndex: 0,
			tabPath: pageRoutes.teamSettingsOverviewRoute,
		},
		{
			label: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
				? teamHeaderTabsStrings.TEAM_SETTINGS_PHYSICAL_CARDS_HEADER_TAB
				: teamHeaderTabsStrings.TEAM_SETTINGS_SOURCE_CARDS_HEADER_TAB,
			link: pageRoutes.teamSettingsSourceCardsRoute,
			tabIndex: 1,
			tabPath: pageRoutes.teamSettingsSourceCardsRoute,
		},
		{
			label: teamHeaderTabsStrings.TEAM_SETTINGS_PEOPLE_HEADER_TAB,
			link: pageRoutes.teamSettingsPeopleRoute,
			tabIndex: 2,
			tabPath: pageRoutes.teamSettingsPeopleRoute,
		},
	];

	if (isAmexCardProvider) {
		teamSettingsTabs.push({
			label: teamHeaderTabsStrings.TEAM_SETTINGS_CODING_LISTS_HEADER_TAB,
			link: pageRoutes.teamSettingsCodingListsRoute,
			tabIndex: 3,
			tabPath: pageRoutes.teamSettingsCodingListsRoute,
		});
	}

	return {
		COMPANY_SETTINGS: companySettingsTabs,
		TEAM_SETTINGS: teamSettingsTabs,
	};
};

export const pageHeaders = {
	COMPANY_SETTINGS: 'COMPANY_SETTINGS',
	TEAM_SETTINGS: 'TEAM_SETTINGS',
} as const;

export const payments = {
	authPaddingPercentage: 7,
	subscriptionPaddingPercentage: 2,
	companyProperties: { providerLimit: 99_999_999 },
};

export const subscriptionEndings = [
	{
		key: 'on-a-specific-date',
		label: 'On a specific date',
	},
	{
		key: 'number-of-payments',
		label: 'Number of payments',
	},
];

export const subscriptionFrequencies = [
	{
		key: 'DAILY',
		label: 'Every day',
	},
	{
		key: 'WEEKLY',
		label: 'Every week',
	},
	{
		key: 'MONTHLY',
		label: 'Every month',
	},
	{
		key: 'QUARTERLY',
		label: 'Every quarter',
	},
	{
		key: 'YEARLY',
		label: 'Every year',
	},
];

export const companyFeaturesConstants = {
	enableTransactionReceiptUpload: 'ENABLE_TRANSACTION_RECEIPT_UPLOAD',
	enableDefaultExpirationDate: 'ENABLE_VIRTUAL_CARD_EXP_RANGE',
	enableDomainWhitelisting: 'ENABLE_DOMAIN_WHITELISTING',
	pauseVirtualCardCreation: 'PAUSE_VIRTUAL_CARD_CREATION',
	enableCompanyPeopleView: 'ENABLE_COMPANY_PEOPLE_VIEW',
	enableGlobalization: 'ENABLE_GLOBALIZATION',
	enableProviderMapping: 'ENABLE_PROVIDER_MAPPING',
};

export const companyPropertiesConstants = {
	defaultExpirationDate: 'tsgo-virtualcardexprange',
	whiteListedDomains: 'whitelisteddomains',
	companyApprovalLimit: 'tsgo-requestlimit',
	cardprovider: 'tsgo-cardprovider',
	showactivity: 'tsgo-showactivity',
} as const;

export const companyPropertyKeys = {
	defaultManager: 'tsgo-defaultmanager',
	requestLimit: 'tsgo-requestlimit',
	virtualCardExpRange: 'tsgo-virtualcardexprange',
};

export const companyPropertiesExtendedConstants = {
	addWhiteListedDomain: 'add-whitelisteddomains',
	removeWhiteListedDomain: 'remove-whitelisteddomains',
};

type CompanyPropertyDefaultExpirationDate = {
	label: string;
	id: number;
};

export const companyPropertyDefaultExpirationDateConstants: NonEmptyArray<CompanyPropertyDefaultExpirationDate> = [
	{
		label: '1 month',
		id: 30,
	},
	{
		label: '3 months',
		id: 90,
	},
	{
		label: '6 months',
		id: 180,
	},
	{
		label: '1 year',
		id: 360,
	},
];

export const teamPropertiesConstant = {
	requestLimit: 'REQUEST_LIMIT',
	enableSingleUse: 'ENABLE_SINGLEUSE',
	enableMultiUse: 'ENABLE_MULTIUSE',
	enableSubscriptions: 'ENABLE_SUBSCRIPTIONS',
	pauseVirtualCardCreation: 'PAUSE_VIRTUAL_CARD_CREATION',
};

export const featuresConstant = {
	ENABLE_CHROME_EXTENSION_LINK: 'Enable chrome extension link',
	ENABLE_COMPANY_PEOPLE_VIEW: 'Enable people view for company admin users',
	ENABLE_DOMAIN_WHITELISTING: 'Enable email domain whitelisting for invited users',
	ENABLE_FILTERED_BY_COSTCENTER: 'Enable filtering by costcenter',
	ENABLE_GLOBALIZATION: 'Enable globalization option in user aside',
	ENABLE_MULTIUSE: 'Enable multi use virtual cards',
	ENABLE_OUT_OF_OFFICE_FORWARDING: 'Enable automatic forwarding of approval requests',
	ENABLE_SINGLEUSE: 'Enable single use virtual cards',
	ENABLE_SUBSCRIPTIONS: 'Enable subscription virtual cards',
	ENABLE_TRANSACTION_RECEIPT_UPLOAD: 'Enable receipt upload for transactions',
	ENABLE_USER_APPROVAL_LIMITS: 'Approvers have a maximum approval limit',
	ENABLE_VIRTUAL_CARD_EXP_RANGE: 'Enable maximum expiration date for virtual card',
	PAUSE_VIRTUAL_CARD_CREATION: 'Pause new virtual cards from being issued',
	ENABLE_ALTERNATE_GET_USER_FUNCTIONS: 'Enable master branch link',
	ENABLE_API_INTEGRATION: 'Enable API integration',
};

export const teamFeaturesConstants = {
	enableMutiUseVirtualCard: 'ENABLE_MULTIUSE',
	enableSingleUseVirtualCard: 'ENABLE_SINGLEUSE',
	enableSubscriptionVirtualCard: 'ENABLE_SUBSCRIPTIONS',
	requestLimit: 'REQUEST_LIMIT',
	pauseVirtualCardCreation: 'PAUSE_VIRTUAL_CARD_CREATION',
};

export const teamSettingsErrorFields = {
	teamName: 'TEAM_NAME',
	requestLimit: 'REQUEST_LIMIT',
	cardOptions: 'CARD_OPTIONS',
};

export const teamRoleConstants = {
	TEAM_ADMIN: teamAdmin,
	TEAM_MANAGER: manager,
	TEAM_MEMBER: teamMember,
};

export const cardTypes = [
	{
		feature: 'ENABLE_SINGLEUSE',
		cardType: 'single-use',
		label: 'Single Use',
	},
	{
		feature: 'ENABLE_MULTIUSE',
		cardType: 'multiple-use',
		label: 'Multi Use',
	},
	{
		feature: 'ENABLE_SUBSCRIPTIONS',
		cardType: 'recurring-use',
		label: 'Subscription',
	},
];

export const virtualCardTypes = [
	{
		key: 'single-use',
		label: 'Single Use',
		cardsKey: 'SINGLE_USE',
	},
	{
		key: 'multiple-use',
		label: 'Multiple Use',
		cardsKey: 'MULTIPLE_USE',
	},
	{
		key: 'recurring-use',
		label: 'Subscription',
		cardsKey: 'RECURRING_USE',
	},
];

export const virtualCardExpRanges = [
	{
		label: '1 month',
		id: 30,
	},
	{
		label: '3 months',
		id: 90,
	},
	{
		label: '6 months',
		id: 180,
	},
	{
		label: '1 year',
		id: 360,
	},
];

export const overviewConstants = {
	COMPANY_ADMIN: companyAdmin,
	TEAM_ADMIN: teamAdmin,
	SECURITY_GROUP_TYPE_COMPANY: 'company',
};

export const teams = {
	USERLIMIT: 180,
};

export const userStateToStatus = {
	ACTIVE: 'Active',
	CREATED: 'Invited',
	LOCKED: 'Removed',
};

export const requestStatus = {
	APPROVED: 'APPROVED',
	CREATED: 'CREATED',
	REJECTED: 'REJECTED',
	SUBMITTED: 'SUBMITTED',
} as const;

const outOfOffice = {
	oooEnabled: 'OOO_ENABLED',
	oooStartDate: 'OOO_START_DATE',
	oooEndDate: 'OOO_END_DATE',
	oooForwardToFullName: 'OOO_FORWARD_TO_FULL_NAME',
	oooForwardTo: 'OOO_FORWARD_TO',
	oooForwardToEmail: 'OOO_FORWARD_TO_EMAIL',
};

export const userTeamProperties = {
	requestLimit: 'REQUEST_LIMIT',
	outOfOffice,
	all: Object.assign(
		{
			defaultApprover: 'DEFAULT_APPROVER',
			invitedDate: 'INVITED_DATE',
			requestLimit: 'REQUEST_LIMIT',
		},
		outOfOffice,
	),
};

export const codingListConstants = {
	predefinedCodingLists: [
		{
			id: 'costcentercode',
			code: '103',
			target: 'VirtualCreditCardRequest',
			name: 'Cost center code',
			description: 'Cost center code',
			group: 'costcentercode',
			ordering: '10',
			defaultEnabled: false,
		},
		{
			id: 'departmentcode',
			code: '102',
			target: 'VirtualCreditCardRequest',
			name: 'Department code',
			description: 'Department code',
			group: 'departmentcode',
			ordering: '20',
			defaultEnabled: false,
		},
		{
			id: 'expendituretypecode',
			code: '122',
			target: 'VirtualCreditCardRequest',
			name: 'Expenditure type',
			description: 'Expenditure type',
			group: 'expendituretypecode',
			ordering: '30',
			defaultEnabled: false,
		},
		{
			id: 'glcode',
			code: '105',
			target: 'VirtualCreditCardRequest',
			name: 'General ledger code',
			description: 'General ledger code',
			group: 'glcode',
			ordering: '40',
			defaultEnabled: false,
		},
	],
	customCodingListTemplate: ({ codingListName, codingListId }) => ({
		id: codingListId,
		code: codingListId,
		target: 'VirtualCreditCardRequest',
		name: codingListName,
		description: codingListName,
		group: codingListId,
		ordering: '80',
	}),
	codingList: 'CODING_LIST',
	codingListPublished: 'CODING_LIST_PUBLISHED',
	providerMapping: 'PROVIDER_MAPPING',
	providerMappingOptions: [
		'Coding 1',
		'Coding 2',
		'Coding 3',
		'Coding 4',
		'Coding 5',
		'Coding 6',
		'Coding 7',
		'Coding 8',
		'Not mapped',
	],
	providerMappingNoneOption: 'notmapped',
	codingListTableId: 'codingListTable',
	teamCodingListTableId: 'teamCodingListTable',
	codingListTeamSelectorAsideId: 'codingListTeamSelectorAside',
	codingListNewEntryTeamSelectorAsideId: 'codingListNewEntryTeamSelectorAside',
	codingListCreateNewListAsideId: 'codingListCreateNewListAside',
	codingListCreateNewCustomListAsideId: 'codingListCreateNewCustomListAside',
	codingListRemoveEntryConfirmationAsideId: 'codingListRemoveEntryConfirmationAside',
	codingListRemoveListConfirmationAsideId: 'codingListRemoveListConfirmationAside',
	codingListRenameListAsideId: 'codingListRenameListAside',
	codingListRenameListInput: 'rename-custom-coding-list',
	codingListRenameListButton: 'rename-custom-coding-list-button',
	codingListRenameListCancelButton: 'rename-custom-coding-list-cancel-button',
	codingListCreateNewCustomListInput: 'create-new-custom-list-input',
	codingListCreateNewCustomListButton: 'create-new-custom-list-button',
	codingListCreateNewCustomListCancelButton: 'create-new-custom-list-cancel-button',
	codingListChangeTeamCancelButton: 'coding-list-change-team-cancel-button',
	codingListTableColumns: [
		{
			label: companySettingsCodingStrings.UNIQUE_IDENTIFIER_COLUMN,
			flex: 4,
		},
		{
			label: companySettingsCodingStrings.CODING_ENTRY_COLUMN,
			flex: 4,
		},
		{
			label: companySettingsCodingStrings.TEAMS_WHO_SEE_THIS_COLUMN,
			flex: 5,
			editable: false,
		},
		{
			label: '',
			editable: false,
		},
		{
			label: '',
			editable: false,
		},
	],
	codingListTeamTableColumns: [
		{
			label: companySettingsCodingStrings.UNIQUE_IDENTIFIER_COLUMN,
			flex: 4,
		},
		{
			label: companySettingsCodingStrings.CODING_ENTRY_COLUMN,
			flex: 8,
		},
		{
			label: '',
			editable: false,
		},
	],
	codingListTableRemoveAction: 'remove-entry',
	codingListTableAddAction: 'add-entry',
	codingListTableChangeTeamAction: 'change-entry-teams',
	codingListTableChangeNewEntryTeamAction: 'change-new-entry-teams',
	codingListTableChangeTeamAllTeamsOption: 'all-teams-option',
	codingListTableChangeTeamOptionTemplate: ({ teamName }) => `change-team-${teamName}`,
	codingListTableChangeTeamSaveButton: 'save-team-changes-button',
	codingListTableToggleButton: 'entry-state-toggle',
};

export const userStatus = {
	ACTIVE: 'ACTIVE',
	CREATED: 'CREATED',
	LOCKED: 'LOCKED',
};

export const cardDetailsAsideProperties = {
	SELECT_TEAMS: 'selectTeams',
	REMOVE_CARD: 'removeCard',
	CANT_REMOVE_CARD: 'cantRemoveCard',
};

export const emptyBillingAddress = {
	addressLine1: '',
	city: '',
	countryCode: '',
	region: '',
	zipCode: '',
};

export const cardsourceStatus = {
	ACTIVE: 'Active',
	IN_PROGRESS: 'Pending',
	INACTIVE: 'Inactive',
	INVALID: 'Invalid',
};

export const sourceCardAsideProperties = {
	ADD_CARD_SOURCE: 'addCardSource',
	CARD_SOURCE_DETAIL: 'cardSourceDetail',
	SELECT_CARD_SOURCE: 'selectCardSource',
};

export const requestConstants = {
	states: {
		approved: 'approved',
		created: 'created',
		deleted: 'deleted',
		new: 'new',
		rejected: 'rejected',
		requested: 'requested',
		submitted: 'submitted',
		sequenceNumber: {
			approved: 3,
			created: 1,
			deleted: -1,
			new: 0,
			rejected: 3,
			requested: 2,
			submitted: 2,
		},
	},
	status: {
		approved: 'Approved',
		created: 'Created',
		deleted: 'Deleted',
		default: 'Requested',
		new: 'Created',
		rejected: 'Declined',
		requested: 'Requested',
		submitted: 'Pending',
	},
	outcomes: {
		approved: 'APPROVED',
		canceled: 'CARD-CANCELED',
		completed: 'COMPLETED',
		reassigned: 'REASSIGNED',
		rejected: 'REJECTED',
		selfReassigned: 'SELF-REASSIGNED',
	},
};

export const companyTeamsPageConstants = ['/team/overview', '/team/cards', '/team/people', '/team/coding'];

export const walletHeaderTabs = {
	company_admin: [
		{
			label: 'All Cards',
			link: '/wallet',
			tabIndex: 0,
			id: 'all',
		},
	],
	team_admin: [
		{
			label: 'My Cards',
			link: '/wallet/my-cards',
			tabIndex: 0,
			id: 'myCards',
		},
		{
			label: 'Team Cards',
			link: '/wallet/team',
			tabIndex: 1,
			id: 'teamCards',
		},
	],
	TEAM_MANAGER: [],
	TEAM_MEMBER: [],
};

export const months = [
	{
		name: 'January',
		value: 0,
	},
	{
		name: 'February',
		value: 1,
	},
	{
		name: 'March',
		value: 2,
	},
	{
		name: 'April',
		value: 3,
	},
	{
		name: 'May',
		value: 4,
	},
	{
		name: 'June',
		value: 5,
	},
	{
		name: 'July',
		value: 6,
	},
	{
		name: 'August',
		value: 7,
	},
	{
		name: 'September',
		value: 8,
	},
	{
		name: 'October',
		value: 9,
	},
	{
		name: 'November',
		value: 10,
	},
	{
		name: 'December',
		value: 11,
	},
];
